import {
  AGENT_IDS,
  PERSONAS,
  fmtEth,
  fmtPct,
  fmtPrice,
  fmtTokens,
  fmtUsd,
  type ProtocolEvent,
  type TimelinePoint,
} from '@quintessence/core';
import { useMemo, useState } from 'react';
import { ChartFrame } from '../charts/ChartFrame';
import { Donut } from '../charts/Donut';
import { HBars } from '../charts/HBars';
import { ScatterGrid } from '../charts/ScatterGrid';
import { StackedArea } from '../charts/StackedArea';
import { TimeChart, type Lane } from '../charts/TimeChart';
import { fmtDateTime } from '../charts/scale';
import { AgentPanel } from '../components/AgentPanel';
import { RegimeBadge } from '../components/Chrome';
import { Feed } from '../components/Feed';
import { NeuralField } from '../components/NeuralField';
import { StatTile } from '../components/StatTile';
import { actionHeadline, mergeFeed, pctChange, sym, useSnapshot } from '../lib';
import { CHART_ORDER, INK, SERIES, SHAPE } from '../theme';

const RANGES = [
  { key: '24h', ms: 24 * 3_600_000 },
  { key: '3d', ms: 3 * 24 * 3_600_000 },
  { key: '7d', ms: 7 * 24 * 3_600_000 },
  { key: 'All', ms: Infinity },
] as const;

type Metric = 'price' | 'mcap' | 'liquidity' | 'holders';

export function Dashboard() {
  const { view, config, events, status } = useSnapshot();
  const [range, setRange] = useState<(typeof RANGES)[number]['key']>('24h');
  const [metric, setMetric] = useState<Metric>('price');
  const [feedFilter, setFeedFilter] = useState<'all' | 'actions' | 'treasury' | 'learning'>('all');
  const symbol = sym(config);

  const feed = useMemo(() => (view ? mergeFeed(view.feed, events, 80) : []), [view, events]);
  const signals = useMemo(() => feed.flatMap((e) => (e.type === 'action' ? [{ id: e.id, agent: e.agent }] : [])), [feed]);

  const timeline = useMemo<TimelinePoint[]>(() => {
    if (!view) return [];
    const span = RANGES.find((r) => r.key === range)!.ms;
    const end = view.timeline[view.timeline.length - 1]?.t ?? view.t;
    return view.timeline.filter((p) => p.t >= end - span);
  }, [view, range]);

  if (!view || !view.market) {
    return (
      <main className="container dash-loading">
        <div className="panel loading-card">
          <div className="spinner" aria-hidden />
          <h2>{status === 'error' ? 'Cannot reach the agents' : 'Waking the agents…'}</h2>
          <p className="muted">The brains are training in the dojo before the market opens. This takes a few seconds.</p>
        </div>
      </main>
    );
  }

  const m = view.market;
  const now = view.t;
  const t0 = timeline[0]?.t ?? now;
  const last24 = view.timeline.filter((p) => p.t >= now - 24 * 3_600_000);
  const spark = (f: (p: TimelinePoint) => number) => last24.map(f);

  const metricSeries = {
    price: { label: `${symbol} price (ETH)`, color: INK.accent, format: (v: number) => fmtPrice(v), get: (p: TimelinePoint) => p.price, log: true },
    mcap: { label: 'Market cap (ETH)', color: INK.accent, format: (v: number) => fmtEth(v, 1), get: (p: TimelinePoint) => p.mcapEth, log: true },
    liquidity: { label: 'Pool liquidity (ETH)', color: INK.accent, format: (v: number) => fmtEth(v, 1), get: (p: TimelinePoint) => p.liquidityEth, log: false },
    holders: { label: 'Holders', color: INK.accent, format: (v: number) => fmtTokens(v), get: (p: TimelinePoint) => p.holders, log: false },
  }[metric];

  const actions = feed.filter((e): e is Extract<ProtocolEvent, { type: 'action' }> => e.type === 'action' && e.t >= t0);
  const lanes: Lane[] = CHART_ORDER.map((id) => ({
    id,
    label: PERSONAS[id].name,
    color: SERIES[id],
    shape: SHAPE[id],
    events: actions
      .filter((a) => a.agent === id)
      .map((a) => ({ t: a.t, title: `${PERSONAS[id].name} ${actionHeadline(a, symbol)}`, detail: `${fmtEth(a.result.ethSpent)} ETH · ${a.size.toLowerCase()} · ${a.thought.regime}` })),
  }));

  const weightsData = timeline.map((p) => ({ t: p.t, values: CHART_ORDER.map((id) => p.weights[AGENT_IDS.indexOf(id)] / 10_000) }));
  const priceChange = pctChange(m.change24h);
  const holdersChange = m.holders24hAgo > 0 ? m.holders / m.holders24hAgo - 1 : 0;

  const learning = CHART_ORDER.map((id) => ({
    id,
    label: PERSONAS[id].name,
    color: SERIES[id],
    points: view.learning.filter((l) => l.agent === id).map((l) => ({ x: l.predicted, y: l.reward, note: `${l.size.toLowerCase()} · ${fmtDateTime(l.t)}` })),
  }));

  const totalRegime = view.regimes.reduce((s, r) => s + r.count, 0) || 1;

  return (
    <main className="dashboard container">
      <div className="dash-head">
        <NeuralField variant="ambient" focus={[0.74, 0.5]} density={4200} signals={signals} className="dash-field" />
        <div>
          <span className="kicker">Mission control</span>
          <h1>The agents, live.</h1>
        </div>
        <div className="dash-head-meta">
          <RegimeBadge name={view.regime.name} />
          <span className="muted">{view.mode === 'sim' ? 'Simulated clock' : 'Updated'} · {fmtDateTime(now)}</span>
        </div>
      </div>

      <div className="filter-row" role="group" aria-label="Time range">
        {RANGES.map((r) => (
          <button key={r.key} type="button" className={range === r.key ? 'active' : ''} aria-pressed={range === r.key} onClick={() => setRange(r.key)}>
            {r.key}
          </button>
        ))}
        <span className="muted filter-note">Range applies to the charts below.</span>
      </div>

      <section className="kpis">
        <div className="hero-tile panel">
          <span className="stat-label">ETH harvested from fees</span>
          <span className="hero-figure">{fmtEth(view.stats.totalHarvested, 3)}</span>
          <span className="muted">
            {fmtEth(m.feesEth24h, 3)} ETH in the last 24h · {view.stats.actions.toLocaleString()} on-chain actions · {view.stats.decisions.toLocaleString()} decisions
          </span>
        </div>
        <StatTile
          label="Price"
          value={fmtUsd(m.priceEth * m.ethUsd)}
          delta={{ value: priceChange, text: fmtPct(priceChange), period: '24h' }}
          trend={spark((p) => p.priceUsd)}
          hint={`${fmtPrice(m.priceEth)} ETH`}
        />
        <StatTile label="Market cap" value={fmtUsd(m.mcapEth * m.ethUsd)} trend={spark((p) => p.mcapEth)} hint={`${fmtEth(m.mcapEth)} ETH`} />
        <StatTile label={m.graduated ? 'Pool liquidity' : 'Curve reserve'} value={fmtEth(m.liquidityEth, 2)} unit="ETH" trend={spark((p) => p.liquidityEth)} />
        <StatTile
          label="Holders"
          value={m.holders.toLocaleString()}
          delta={{ value: holdersChange, text: fmtPct(holdersChange), period: '24h' }}
          trend={spark((p) => p.holders)}
        />
        <StatTile
          label="Burned by PYRE"
          value={fmtTokens(view.stats.totalBurned)}
          unit={symbol}
          hint={`${fmtPct(view.stats.totalBurned / m.totalSupply, 2, false)} of supply`}
          accent={SERIES.pyre}
        />
        <StatTile label="HOARD reserve" value={fmtTokens(view.stats.buybackHeld)} unit={symbol} accent={SERIES.hoard} />
        <StatTile
          label="Airdropped by RAIN"
          value={fmtTokens(view.stats.totalDistributed)}
          unit={symbol}
          hint={`${view.stats.airdropRounds} rounds`}
          accent={SERIES.rain}
        />
        <StatTile label="LP added by TIDE" value={fmtEth(view.stats.lpEthAdded, 2)} unit="ETH" accent={SERIES.tide} />
      </section>

      <section className="dash-row two-one">
        <ChartFrame
          title={metricSeries.label}
          subtitle="Each lane below marks an agent's on-chain actions."
          actions={
            <div className="segmented" role="group" aria-label="Metric">
              {(['price', 'mcap', 'liquidity', 'holders'] as Metric[]).map((k) => (
                <button key={k} type="button" className={metric === k ? 'active' : ''} aria-pressed={metric === k} onClick={() => setMetric(k)}>
                  {k === 'mcap' ? 'MCap' : k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
          }
          legend={lanes.map((l) => ({ label: l.label, color: l.color, kind: 'dot' }))}
          table={{
            columns: ['Time (UTC)', metricSeries.label],
            rows: timeline.slice(-60).reverse().map((p) => [fmtDateTime(p.t), metricSeries.format(metricSeries.get(p))]),
          }}
        >
          <TimeChart
            data={timeline.map((p) => ({ t: p.t, v: metricSeries.get(p) }))}
            label={metricSeries.label}
            color={metricSeries.color}
            format={metricSeries.format}
            log={metricSeries.log}
            area
            lanes={lanes}
            height={230}
          />
        </ChartFrame>

        <ChartFrame
          title="Council split"
          subtitle="Share of every harvest routed to each agent."
          legend={CHART_ORDER.map((id) => ({ label: PERSONAS[id].name, color: SERIES[id] }))}
          table={{
            columns: ['Agent', 'Weight', 'Performance score'],
            rows: CHART_ORDER.map((id) => [PERSONAS[id].name, `${(view.council.weights[AGENT_IDS.indexOf(id)] / 100).toFixed(1)}%`, view.council.efficiency[id].toFixed(2)]),
          }}
        >
          <div className="council-now">
            <Donut
              segments={CHART_ORDER.map((id) => ({
                label: PERSONAS[id].name,
                value: view.council.weights[AGENT_IDS.indexOf(id)],
                color: SERIES[id],
                detail: `${(view.council.weights[AGENT_IDS.indexOf(id)] / 100).toFixed(1)}%`,
              }))}
              center={`${view.council.history.length}`}
              centerSub="rebalances"
            />
            <ul className="council-list">
              {CHART_ORDER.map((id) => (
                <li key={id}>
                  <i className="legend-key legend-rect" style={{ background: SERIES[id] }} />
                  <span>{PERSONAS[id].name}</span>
                  <strong>{(view.council.weights[AGENT_IDS.indexOf(id)] / 100).toFixed(1)}%</strong>
                  <span className="muted">score {view.council.efficiency[id].toFixed(2)}</span>
                </li>
              ))}
            </ul>
          </div>
          <StackedArea data={weightsData} labels={CHART_ORDER.map((id) => PERSONAS[id].name)} colors={CHART_ORDER.map((id) => SERIES[id])} format={(v) => `${Math.round(v * 100)}%`} height={120} />
        </ChartFrame>
      </section>

      <section className="agents-grid">
        {view.agents.map((a) => (
          <AgentPanel key={a.id} agent={a} now={now} config={config} />
        ))}
      </section>

      <section className="dash-row one-one">
        <ChartFrame
          title="Fee inflow"
          subtitle="Creator-fee ETH reaching the treasury over the trailing 24 hours."
          table={{ columns: ['Time (UTC)', 'Fees, trailing 24h (ETH)'], rows: timeline.slice(-60).reverse().map((p) => [fmtDateTime(p.t), fmtEth(p.feesEth24h)]) }}
        >
          <TimeChart data={timeline.map((p) => ({ t: p.t, v: p.feesEth24h }))} label="Fees, trailing 24h" color={INK.accent} format={(v) => fmtEth(v, 2)} area zero height={180} />
        </ChartFrame>
        <ChartFrame
          title="Are the brains right?"
          subtitle="Each dot is a matured decision: what the brain predicted vs the reward it actually earned."
          table={{
            columns: ['Agent', 'Size', 'Predicted', 'Realised'],
            rows: view.learning
              .slice(-40)
              .reverse()
              .map((l) => [PERSONAS[l.agent].name, l.size.toLowerCase(), l.predicted.toFixed(3), l.reward.toFixed(3)]),
          }}
        >
          <ScatterGrid panels={learning} xLabel="predicted" yLabel="realised" />
        </ChartFrame>
      </section>

      <section className="dash-row feed-row">
        <div className="panel feed-panel">
          <div className="chart-head">
            <div>
              <h3 className="chart-title">Live activity</h3>
              <p className="chart-sub">Every harvest, action, rebalance, lesson and evolution, as it happens.</p>
            </div>
            <div className="segmented" role="group" aria-label="Filter activity">
              {(['all', 'actions', 'treasury', 'learning'] as const).map((k) => (
                <button key={k} type="button" className={feedFilter === k ? 'active' : ''} aria-pressed={feedFilter === k} onClick={() => setFeedFilter(k)}>
                  {k[0].toUpperCase() + k.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="feed-scroll">
            <Feed
              events={feed
                .filter((e) =>
                  feedFilter === 'all'
                    ? true
                    : feedFilter === 'actions'
                      ? e.type === 'action' || e.type === 'graduation'
                      : feedFilter === 'treasury'
                        ? e.type === 'harvest' || e.type === 'council'
                        : e.type === 'lesson' || e.type === 'evolution',
                )
                .slice(0, 50)}
              now={now}
              config={config}
              empty="Nothing of this kind yet."
            />
          </div>
        </div>
        <div className="side-stack">
          <ChartFrame
            title="Market regimes"
            subtitle="How often the market has been in each mood."
            table={{ columns: ['Regime', 'Observations', 'Mood'], rows: view.regimes.map((r) => [r.name, r.count, r.mood]) }}
          >
            <HBars
              items={[...view.regimes].sort((a, b) => b.count - a.count).map((r) => ({ label: r.name === view.regime.name ? `${r.name} ●` : r.name, value: r.count / totalRegime, note: r.mood }))}
              color={INK.accent}
              format={(v) => fmtPct(v, 0, false)}
            />
          </ChartFrame>
          <div className="panel lessons">
            <h3 className="chart-title">What the agents have learned</h3>
            <ul>
              {view.agents
                .flatMap((a) => a.brain.lessons.slice(0, 2).map((l) => ({ ...l, agent: a.id })))
                .sort((a, b) => b.confidence - a.confidence)
                .slice(0, 6)
                .map((l) => (
                  <li key={`${l.agent}-${l.id}`}>
                    <i className="legend-key legend-dot" style={{ background: SERIES[l.agent] }} />
                    <p>
                      <strong>{PERSONAS[l.agent].name}</strong> {l.text} <span className="muted">· {Math.round(l.confidence * 100)}%</span>
                    </p>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      </section>
    </main>
  );
}
